import { useLocalSearchParams } from "expo-router";
import { useMemo } from "react";
import { hasListeningItems } from "../helpers/content";
import { isIntroScreen } from "../helpers/intro-screens";
import { getPartIndexForBlock } from "../helpers/session";
import useSessionStore from "./useSessionStore";

const useCurrentSession = () => {
	const sessionItems = useSessionStore((state) => state.sessionItems);
	const currentBlockIndex = useSessionStore((state) => state.currentBlockIndex);
	const parts = useSessionStore((state) => state.parts);
	const { index } = useLocalSearchParams<{
		index: string;
	}>();

	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;
	const hasListeningItemsResult = useMemo(() => {
		return hasListeningItems(sessionItems ?? []);
	}, [sessionItems]);

	const currentBlock = sessionItems?.length
		? sessionItems[currentBlockIndex]
		: [];

	const currentItem = currentBlock[currentItemIndex];
	const numSessionItems = sessionItems?.length ?? 0;

	const numTotalItems = sessionItems?.reduce((acc, curr) => {
		return acc + curr.length;
	}, 0);

	const numTotalParts = parts.length;
	// Intro-only blocks are not registered in any Part's blockIndices, so
	// resolve their Part from the intro item's own part metadata.
	const blockPartIndex = getPartIndexForBlock(parts, currentBlockIndex);
	const itemPartIndex = parts.findIndex(
		(p) => p.partId === Number(currentItem?.metadata?.part ?? 0),
	);
	const currentPartIndex = parts.length
		? Math.max(0, blockPartIndex >= 0 ? blockPartIndex : itemPartIndex)
		: 0;
	const currentPart = parts[currentPartIndex];
	const isLastBlockInPart = currentPart
		? currentBlockIndex ===
			currentPart.blockIndices[currentPart.blockIndices.length - 1]
		: true;
	const sectionIndexWithinPart = currentPart
		? Math.max(0, currentPart.blockIndices.indexOf(currentBlockIndex))
		: 0;

	const displayItemIndex = useMemo(() => {
		if (isIntroScreen(currentItem)) {
			return currentItemIndex + 1;
		}
		const nonScreenItems = currentBlock.filter((item) => !isIntroScreen(item));
		const position = nonScreenItems.findIndex(
			(item) => item.id === currentItem?.id,
		);
		return position >= 0 ? position + 1 : 0;
	}, [currentBlock, currentItem, currentItemIndex]);

	return {
		currentBlock,
		currentItem,
		currentBlockIndex,
		currentItemIndex,
		numSessionItems,
		numTotalItems,
		hasListeningItems: hasListeningItemsResult,
		parts,
		currentPart,
		currentPartIndex,
		numTotalParts,
		isLastBlockInPart,
		sectionIndexWithinPart,
		displayItemIndex,
	};
};

export default useCurrentSession;
